import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getAccessProductsGrid } from '../utils';
import { BasePagination } from 'api/common-schemas';
import Products, { getPageSize } from '../BaseComponents/Products/Products';
import { ProductCategory, ProductType } from '../accessExpress.types';
import { useCartContext } from '../CartProvider';

const Access = observer(() => {
  const [page_size, setPageSize] = useState(getPageSize());
  const cart = useCartContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<number>(
    location.state?.page ?? 0,
  );
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleResize = () => setPageSize(getPageSize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData([
    'order-service-access',
    currentPage + 1,
    searchParams.get('search'),
    searchParams.get('sort'),
  ]);

  const _getProducts = () => {
    if (cachedData)
      return Promise.resolve(cachedData as BasePagination<ProductType>);

    return getAccessProductsGrid({
      page: currentPage + 1,
      page_size: page_size,
      search: searchParams.get('search') ?? '',
      sort_order: (searchParams.get('sort') as 'asc' | 'desc') ?? '',
    });
  };

  const { data, isFetching } = useQuery(
    [
      'order-service-access',
      currentPage + 1,
      searchParams.get('search'),
      searchParams.get('sort'),
      page_size,
    ],
    _getProducts,
    { keepPreviousData: true, refetchOnWindowFocus: false },
  );
  const [categoryActive, setCategoryActive] = useState<
    ProductCategory | null | undefined
  >(cart.selectedAccessProductCategory);

  const onProductClick = (product: ProductType) => {
    setCategoryActive(product.category);
    cart.selectAccessProductCategory(product.category);
  };
  useEffect(() => {
    if (!cart.isOpen) setCategoryActive(undefined);
  }, [cart.isOpen]);

  const onChangePageHistory = (page: number) => {
    navigate(location, {
      state: { page },
      replace: true,
      preventScrollReset: true,
    });
  };

  return (
    <div className="mt-6 flex flex-1 flex-col">
      <Products
        price_type="starting"
        onProductClick={onProductClick}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isLoading={isFetching}
        onChangePageHistory={onChangePageHistory}
        products={data?.data ?? []}
        total_pages={data?.metadata?.total_pages ?? 0}
        page={data?.metadata.page}
        page_size={(data?.data.length as 6) || 4}
        categoryActive={categoryActive}
      />
    </div>
  );
});

export default Access;
